import type { AppSettings, CalendarEvent, CalendarView, ThemeMode } from '../types'
import { SETTINGS_KEY, STORAGE_KEY } from '../config'

function nowIso(): string {
  return new Date().toISOString()
}

export function createId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

function normalizeEvent(raw: unknown): CalendarEvent | null {
  if (!raw || typeof raw !== 'object') return null
  const e = raw as Partial<CalendarEvent>
  if (
    typeof e.id !== 'string' ||
    typeof e.title !== 'string' ||
    typeof e.start !== 'string' ||
    typeof e.end !== 'string' ||
    typeof e.color !== 'string'
  ) {
    return null
  }
  return {
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    notes: typeof e.notes === 'string' ? e.notes : undefined,
    color: e.color,
    updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : nowIso(),
    deletedAt: e.deletedAt ?? null,
  }
}

export function loadEvents(): CalendarEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map(normalizeEvent).filter((e): e is CalendarEvent => Boolean(e))
  } catch {
    return []
  }
}

export function saveEvents(events: CalendarEvent[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events))
}

const DEFAULT_SETTINGS: AppSettings = {
  activeTab: 'kalender',
  calendarView: 'month',
  theme: 'dark',
  updatedAt: nowIso(),
}

export function loadSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    const parsed = JSON.parse(raw) as Partial<AppSettings>
    const view: CalendarView =
      parsed.calendarView === 'year' ||
      parsed.calendarView === 'month' ||
      parsed.calendarView === 'week' ||
      parsed.calendarView === 'day'
        ? parsed.calendarView
        : 'month'
    const theme: ThemeMode = parsed.theme === 'light' ? 'light' : 'dark'
    return {
      activeTab: typeof parsed.activeTab === 'string' ? parsed.activeTab : 'kalender',
      calendarView: view,
      theme,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : nowIso(),
    }
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
}
